import { FileWithPath } from 'react-dropzone'
import {
  BlockType,
  CellBlock,
  DateFormat,
  FolderItem,
  NextCell,
  OfficeRuntimeAccessToken,
  OfficeRuntimeTokenJwtPayload,
  Reference,
  Template,
  TextBlock,
  TextcutRectangle,
  UserSettings,
} from '../types'

export const isCell = (block: TextBlock): block is CellBlock =>
  block.blockType === BlockType.CELL

export const isReferece = (item: any): item is Reference => {
  const keys = Object.keys(item)
  const set = new Set(keys)
  return (
    set.has('fileId') &&
    set.has('filePage') &&
    set.has('type') &&
    set.has('blockId')
  )
}

export const isTextBlock = (block: any): block is TextBlock => {
  if (block == null || typeof block !== 'object') return false
  const keys = Object.keys(block)
  const set = new Set(keys)
  return (
    set.has('id') &&
    set.has('blockType') &&
    set.has('description') &&
    set.has('boundingPoly')
  )
}

export const isDataMatchReference = (block: any): block is TextBlock => {
  const keys = Object.keys(block)
  const set = new Set(keys)
  return (
    isTextBlock(block) &&
    set.has('fileId') &&
    set.has('filePage') &&
    set.has('row') &&
    set.has('col') &&
    set.has('startRow') &&
    set.has('startCol') &&
    set.has('sheetId')
  )
}

export const eventTargetIsNode = (
  target: EventTarget | null
): target is HTMLElement => (target && 'nodeType' in target ? true : false)

export const isCustomEvent = (event: Event): event is CustomEvent =>
  'detail' in event
export const fileWithPathGuard = (file: File): file is FileWithPath =>
  'path' in file

export const isOfficeError = (error: any): error is Office.Error => {
  if (error == null) return false
  if (typeof error === 'object') {
    return 'name' in error && 'message' in error && 'code' in error
  }
  return false
}

export const isOfficeRuntimeToken = (
  token: OfficeRuntimeTokenJwtPayload
): token is OfficeRuntimeAccessToken =>
  'name' in token && 'preferred_username' in token && 'oid' in token

export const isTextcutRect = (any: any): any is TextcutRectangle => {
  const requires = [
    'x',
    'y',
    'w',
    'h',
    'fileId',
    'filePage',
    'rangeAddr',
    'sheetId',
    'degree',
    'ocrH',
    'ocrW',
    'tag',
    'bindingId',
    'sheetName',
    'fill',
    'stroke',
    'values',
    'cellColor',
  ]

  for (const require of requires) {
    if (require in any) continue
    else return false
  }
  return true
}

export const isDialogMessageArg = (
  arg: any
): arg is {
  message: string
  origin: string | undefined
} => 'message' in arg

export const isStringArray = (arr: unknown): arr is string[] => {
  if (!Array.isArray(arr)) return false
  return arr.every((elem) => typeof elem === 'string')
}

export const isUserSetting = (data: unknown): data is UserSettings => {
  if (typeof data !== 'object' || data === null) return false

  const obj = data as Record<string, unknown>

  return (
    typeof obj.firmId === 'string' &&
    typeof obj.email === 'string' &&
    typeof obj.userId === 'string' &&
    typeof obj.createdAt === 'string' &&
    typeof obj.updatedAt === 'string' &&
    typeof obj.nextCell === 'string' &&
    Object.values(NextCell).includes(obj.nextCell as NextCell) &&
    typeof obj.dateFormat === 'string' &&
    Object.values(DateFormat).includes(obj.dateFormat as DateFormat)
  )
}

export const isTemplate = (obj: any): obj is Template => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof obj.templateId === 'string' &&
    typeof obj.title === 'string' &&
    typeof obj.description === 'string' &&
    Array.isArray(obj.valueItems) &&
    Array.isArray(obj.labelItems) &&
    typeof obj.searchMode === 'string'
  )
}

export const isFolderItem = (item: unknown): item is FolderItem => {
  if (typeof item !== 'object' || item === null) return false
  const obj = item as Record<string, unknown>
  return (
    typeof obj.id === 'string' &&
    typeof obj.name === 'string' &&
    (typeof obj.parentFolderId === 'string' || obj.parentFolderId === undefined) &&
    typeof obj.createdAt === 'string'
  )
}
